import React from 'react';
import { AlertCircle, Check, Pencil } from 'lucide-react';
import { Button } from '../../ui/button';
import { cn } from '../../ui/utils';
import type { PerformanceScheduleDetail } from '../../../types/performance.types';
import {
  formatPerformanceDate,
  reviewWindowLabel,
} from '../../../utils/performance';
import { PerformancePill } from '../PerformancePrimitives';

export const OverviewTab: React.FC<{
  detail: PerformanceScheduleDetail;
  onEditSchedule: () => void;
}> = ({ detail, onEditSchedule }) => {
  const { schedule, checks } = detail;
  const rows = [
    {
      label: 'Goal setting',
      value: `${formatPerformanceDate(schedule.goalOpenDate)} – ${formatPerformanceDate(schedule.goalCloseDate)} · ${schedule.minGoals}–${schedule.maxGoals} goals · ${schedule.minWeight}–${schedule.maxWeight}% each`,
    },
    {
      label: 'Mid-year review',
      value: schedule.mid.enabled
        ? `${reviewWindowLabel(schedule.mid)} · self ${formatPerformanceDate(schedule.mid.selfStart)}–${formatPerformanceDate(schedule.mid.selfEnd)} · manager ${formatPerformanceDate(schedule.mid.managerStart)}–${formatPerformanceDate(schedule.mid.managerEnd)}`
        : 'Off',
    },
    {
      label: 'End-of-year review',
      value: schedule.eoy.enabled
        ? `${reviewWindowLabel(schedule.eoy)} · self ${formatPerformanceDate(schedule.eoy.selfStart)}–${formatPerformanceDate(schedule.eoy.selfEnd)} · manager ${formatPerformanceDate(schedule.eoy.managerStart)}–${formatPerformanceDate(schedule.eoy.managerEnd)}`
        : 'Off',
    },
    {
      label: 'Rating scale',
      value: `${detail.levels.length} levels · ${detail.levels.map((level) => level.label).join(' / ')}`,
    },
    {
      label: 'Goal categories',
      value: detail.categories
        .filter((category) => category.active)
        .map((category) => category.name)
        .join(', '),
    },
    {
      label: 'Participants',
      value: `${detail.metrics.participants} included · ${detail.metrics.excluded} excluded`,
    },
  ];

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold">Overview</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              {schedule.status === 'Draft'
                ? 'Draft — invisible to employees until activated. Resolve the checklist, then activate.'
                : schedule.status === 'Closed'
                  ? 'Closed cycle. Every screen is read-only and results are visible to employees.'
                  : 'The schedule is live. Dates drive every phase; nothing is stored per phase.'}
            </p>
          </div>
          {schedule.status !== 'Closed' ? (
            <Button variant="outline" size="sm" onClick={onEditSchedule}>
              <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit dates
            </Button>
          ) : null}
        </div>

        <div className="mt-4 divide-y divide-border">
          {rows.map((row) => (
            <div key={row.label} className="grid gap-1 py-2 sm:grid-cols-[200px_minmax(0,1fr)]">
              <span className="text-xs font-medium text-muted-foreground">{row.label}</span>
              <span className="text-sm text-foreground">{row.value || '—'}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold">Activation checklist</h2>
            <p className="text-sm text-muted-foreground">
              {detail.failingChecks
                ? `${detail.failingChecks} item${detail.failingChecks === 1 ? '' : 's'} to resolve before this schedule can be activated.`
                : 'All checks pass.'}
            </p>
          </div>
          {detail.failingChecks ? (
            <PerformancePill tone="warning">
              {detail.failingChecks} outstanding
            </PerformancePill>
          ) : (
            <PerformancePill tone="success">Ready</PerformancePill>
          )}
        </div>
        <ul className="mt-3 space-y-2">
          {checks.map((check) => (
            <li key={check.key} className="flex items-start gap-2 text-sm">
              <span
                className={cn(
                  'mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded-full border',
                  check.ok
                    ? 'border-success-border bg-success-bg text-success'
                    : 'border-warning-border bg-warning-bg text-warning'
                )}
              >
                {check.ok ? <Check className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
              </span>
              <span>
                <span className="font-medium text-foreground">{check.label}</span>
                <span className="block text-xs text-muted-foreground">{check.detail}</span>
              </span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
